import {
	DropdownMenuPortal,
	Root as DropdownMenuRoot,
	DropdownMenuTrigger
} from '@radix-ui/react-dropdown-menu'
import { useLocation, useNavigate } from 'react-router'
import { DropdownContent, DropdownItem } from '@/components/ui/dropdown-atoms'
import { DotsHorizontalIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import {
	type CheckInAction,
	type CheckInActionId,
	checkInActionToLabelKey
} from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import type { MarqueeAttendee } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

interface CheckInRowMenuProps {
	attendee: MarqueeAttendee
	/**
	 * The row's actions, already filtered to the ones the event's state allows
	 * and minus the one the row draws inline. The caller owns both, so this menu
	 * never offers an action the backend would refuse nor repeats the button
	 * sitting next to it.
	 */
	actions: CheckInAction[]
	/**
	 * Attendee-detail route, when there is one. Only a tune-up has the page, so
	 * on a presentation the "See details" item is simply absent.
	 */
	detailPath?: string
	onAction: (attendee: MarqueeAttendee, action: CheckInActionId) => void
	/** Which action is in flight for this row, so the spinner lands correctly. */
	pending: CheckInActionId | null
	/**
	 * Open state, owned by the list. It has to outlive this component: the action
	 * column's cells are rebuilt when an action starts, so a menu holding its own
	 * state would close on the click that starts the wait it is reporting.
	 */
	open: boolean
	onOpenChange: (open: boolean) => void
}

/**
 * The desktop row's kebab: everything the row can do that is not already the
 * inline button beside it, so the action column stays one button plus one menu
 * instead of a link cluster that changes width per state.
 *
 * The popup is portalled, as every other row menu here is: rendered in place it
 * would be clipped by the table's own scroll container.
 *
 * Renders nothing when the row has no action and no detail page, rather than an
 * empty popup.
 */
export function CheckInRowMenu({
	attendee,
	actions,
	detailPath,
	onAction,
	pending,
	open,
	onOpenChange
}: CheckInRowMenuProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const busy = pending !== null

	if (actions.length === 0 && !detailPath) {
		return null
	}

	return (
		<DropdownMenuRoot onOpenChange={onOpenChange} open={open}>
			<DropdownMenuTrigger asChild>
				<button
					aria-label={t('marquee:checkin_row_menu_label')}
					className='flex cursor-pointer items-center justify-center text-neutral-500 hover:text-neutral-700 disabled:cursor-not-allowed disabled:text-neutral-300'
					// Shut while any of the row's writes is in flight, the inline
					// button's included: everything in here answers the same
					// question the running write does, so opening it could only
					// offer a second answer to race the first.
					disabled={busy}
					type='button'
				>
					{/* The design's glyph is dots-vertical; the icon set ships the
					    horizontal one, so it is turned rather than redrawn. */}
					<DotsHorizontalIcon className='rotate-90' size={20} />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownContent align='end' className='w-56' sideOffset={4}>
					{actions.map((action) => {
						const isPending = pending === action.id

						return (
							<DropdownItem
								className={cn(
									'flex items-center gap-2 text-neutral-900 data-disabled:opacity-50',
									action.tone === 'danger' && 'text-error-600'
								)}
								// Never `disabled` on the running item: flipping it on
								// the item that was just selected would hand Radix a
								// focus change mid-select, so the lock is enforced in
								// onSelect instead, where it cannot dismiss the menu
								// the spinner lives in. It also has to keep full
								// strength, being the only thing on screen reporting
								// the wait. Its siblings take the real `disabled`.
								disabled={busy && !isPending}
								key={action.id}
								// preventDefault holds the menu open past the select,
								// which is what gives the spinner a place to appear;
								// the list closes it once the action settles.
								onSelect={(event) => {
									event.preventDefault()
									if (busy) {
										return
									}
									onAction(attendee, action.id)
								}}
							>
								{t(checkInActionToLabelKey[action.id])}
								{isPending && <Spinner size={14} />}
							</DropdownItem>
						)
					})}
					{detailPath && (
						<DropdownItem
							className='text-neutral-900 data-disabled:opacity-50'
							// Navigating away mid-write would leave the roster
							// behind without its answer, so it waits like the rest.
							disabled={busy}
							// Same `state.from` the name link records, so the
							// detail page's back button returns to this tab.
							onClick={() =>
								navigate(detailPath, {
									state: { from: pathname }
								})
							}
						>
							{t('marquee:checkin_action_see_details')}
						</DropdownItem>
					)}
				</DropdownContent>
			</DropdownMenuPortal>
		</DropdownMenuRoot>
	)
}
